import React, { useState, useEffect } from 'react';
import { X, Save, User } from 'lucide-react';
import { useData } from '../../contexts/DataContext';

const UserForm = ({ user, onClose, onSubmit }) => {
  const { classes: apiClasses, organizations } = useData();
  const [formData, setFormData] = useState({
    userName: user?.userName || '',
    userEmail: user?.userEmail || '',
    userPhoneNumber: user?.userPhoneNumber || '',
    userRole: user?.userRole || 'teacher',
    school: user?.school || '',
    assignedClasses: user?.assignedClasses || [],
    status: user?.status || 'active',
    employeeId: user?.employeeId || '',
    designation: user?.designation || '',
    department: user?.department || '',
    dateOfJoining: user?.dateOfJoining ? new Date(user.dateOfJoining).toISOString().split('T')[0] : ''
  });

  const [errors, setErrors] = useState({});

  // Set default school from organizations if available
  useEffect(() => {
    const defaultSchool = organizations && organizations.length > 0 ? organizations[0]?.userName : '';
    if (defaultSchool && formData.school !== defaultSchool && !user?.school) {
      setFormData(prev => ({ // eslint-disable-line react-hooks/set-state-in-effect
        ...prev,
        school: defaultSchool
      }));
    }
  }, [organizations, formData.school, user]);

  // Debug: Log available classes
  useEffect(() => {
  }, [apiClasses]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.userName?.trim()) newErrors.userName = 'userName is required';
    if (!formData.userEmail?.trim()) newErrors.userEmail = 'userEmail is required';
    if (!formData.userPhoneNumber?.trim()) newErrors.userPhoneNumber = 'userPhoneNumber is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit({
        ...formData,
        _id: user?._id || Date.now(),
        whatsappVerified: false,
        lastLogin: new Date().toISOString(),
        createdAt: user?.createdAt || new Date().toISOString()
      });
    }
  };

  const userRoles = [
    { value: 'admin', label: 'Administrator' },
    { value: 'teacher', label: 'Teacher' }
  ];

  // Transform API classes to match form expectations
  const classes = apiClasses.map(cls => ({
    value: cls._id,
    label: cls.unitName
  }));

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-1.5 rounded" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)' }}>
              <User className="h-4 w-4" style={{ color: 'var(--primary-color, #4f46e5)' }} />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900">
                {user ? 'Edit User' : 'Add New User'}
              </h2>
              <p className="text-[10px] text-gray-500">Fill in the user details below</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name */}
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                name="userName"
                type="text"
                value={formData.userName}
                onChange={handleInputChange}
                className="input-field text-xs py-1.5 px-2"
                placeholder="Enter full name"
              />
              {errors.userName && (
                <p className="mt-0.5 text-xs text-red-600">{errors.userName}</p>
              )}
            </div>

            {/* userEmail */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                userEmail Address *
              </label>
              <input
                name="userEmail"
                type="email"
                value={formData.userEmail}
                onChange={handleInputChange}
                className="input-field text-xs py-1.5 px-2"
                placeholder="user@school.com"
              />
              {errors.userEmail && (
                <p className="mt-0.5 text-xs text-red-600">{errors.userEmail}</p>
              )}
            </div>

            {/* userPhoneNumber */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                user Phone Number *
              </label>
              <input
                name="userPhoneNumber"
                type="tel"
                value={formData.userPhoneNumber}
                onChange={handleInputChange}
                className="input-field text-xs py-1.5 px-2"
                placeholder="+91 98765 43210"
              />
              {errors.userPhoneNumber && (
                <p className="mt-0.5 text-xs text-red-600">{errors.userPhoneNumber}</p>
              )}
            </div>

            {/* userRole */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                userRole *
              </label>
              <select
                name="userRole"
                value={formData.userRole}
                onChange={handleInputChange}
                className="input-field text-xs py-1.5 px-2"
              >
                {userRoles.map(userRole => (
                  <option key={userRole.value} value={userRole.value}>{userRole.label}</option>
                ))}
              </select>
            </div>

            {/* Assigned Classes (for teachers) */}
            {formData.userRole === 'teacher' && (
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Assigned Classes
                </label>
                <div className="max-h-32 overflow-y-auto border border-gray-200 rounded p-2 bg-gray-50">
                  {classes.length === 0 ? (
                    <p className="text-xs text-gray-500">No classes available. Please create classes first.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                      {classes.map(cls => {
                        // Check if this class is selected - handle both string IDs and object formats
                        const isChecked = formData.assignedClasses?.some(c => {
                          if (typeof c === 'string') {
                            return c === cls.value;
                          } else if (typeof c === 'object' && c !== null) {
                            return c._id === cls.value;
                          }
                          return false;
                        }) || false;

                        return (
                          <label key={cls.value} className="flex items-center cursor-pointer hover:bg-gray-100 p-1.5 rounded">
                            <input
                              type="checkbox"
                              name="assignedClasses"
                              value={cls.value}
                              checked={isChecked}
                              onChange={(e) => {
                                const value = e.target.value;
                                const checked = e.target.checked;
                                setFormData(prev => ({
                                  ...prev,
                                  assignedClasses: checked 
                                    ? [...(prev.assignedClasses || []), value]
                                    : (prev.assignedClasses || []).filter(c => {
                                        // Handle both string and object formats when removing
                                        if (typeof c === 'string') {
                                          return c !== value;
                                        } else if (typeof c === 'object' && c !== null) {
                                          return c._id !== value;
                                        }
                                        return true;
                                      })
                                }));
                              }}
                              className="rounded border-gray-300 h-3.5 w-3.5"
                              style={{ accentColor: 'var(--primary-color, #4f46e5)' }}
                            />
                            <span className="ml-2 text-xs text-gray-700">{cls.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Status */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="input-field text-xs py-1.5 px-2"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* HR Data Section - Only for Staff/Admin */}
            <div className="md:col-span-2 border-t pt-4 mt-2">
              <h4 className="text-xs font-bold text-gray-900 mb-3 uppercase tracking-wider">HR & Employment Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Employee ID</label>
                  <input
                    name="employeeId"
                    type="text"
                    value={formData.employeeId}
                    onChange={handleInputChange}
                    className="input-field text-xs py-1.5 px-2"
                    placeholder="EMP001"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Designation</label>
                  <input
                    name="designation"
                    type="text"
                    value={formData.designation}
                    onChange={handleInputChange}
                    className="input-field text-xs py-1.5 px-2"
                    placeholder="Senior Teacher"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Department</label>
                  <input
                    name="department"
                    type="text"
                    value={formData.department}
                    onChange={handleInputChange}
                    className="input-field text-xs py-1.5 px-2"
                    placeholder="Science"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Date of Joining</label>
                  <input
                    name="dateOfJoining"
                    type="date"
                    value={formData.dateOfJoining}
                    onChange={handleInputChange}
                    className="input-field text-xs py-1.5 px-2"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-2 pt-4 border-t border-gray-200 mt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors text-xs font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary text-xs font-medium flex items-center space-x-1.5"
            >
              <Save className="h-3.5 w-3.5" />
              <span>{user ? 'Update User' : 'Add User'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;
